import {HardwareItem} from "../../../../../../api/Hardware";
import {inches} from "../../const";
import {ElevationApp} from "../../ElevationApp";
import {DoorFace, swingSideFor} from "../../scenes/frameProfile/Helpers";
import {CENTER_LOCK_ABOVE_BOTTOM, DEADBOLT_ABOVE_BOTTOM, MdHardwarePlacement, STANDARD_BACKSET} from "./MdHardware";
import {MdFrameSpec} from "../frame/MdFrameSpec";
import {MdDoorSpec} from "../door/MdDoorSpec";
import {MdHinges} from "./MdHinges";
import {ARMOUR_PLATE_HEIGHT, MOP_PLATE_HEIGHT} from "../../shared-2d/hardwareGlyphs/ProtectionPlate";
import {liteOverlapsOf} from "./MdHardwareFootprint";

// Placement rules for door hardware, keyed by product category. A rule on a category
// applies to every item under it in the product category tree; a rule on a deeper
// category overrides, field by field, the rules above it (a field set to null drops the
// ancestor's limit). All distances are scene units (mm); the hub is the placement point
// the dimensions measure to.
export type HardwarePlacementRule = {
    // the product category this rule is attached to, at any level of the tree.
    category: string;
    // "clamp" (the default) keeps drags and dimension edits inside the limits; "flag" lets
    // the placement go anywhere and only marks it red. A placement outside the limits for
    // another reason (a stored design, a resized door) is flagged either way.
    enforce?: "clamp" | "flag";
    // the face of the door the hardware mounts on: the push face (the leaf swings away
    // from it) or the pull face (it swings toward it). A view draws the item only when it
    // shows that face (see doorFace in scenes/frameProfile/Helpers). Unset (or null)
    // shows the item on both faces.
    face?: DoorFace | null;
    // the item is mortised into the door edge and shows on neither face: the leaf faces
    // skip its door placement, and only the latch edge draws it (see drawnOnDoorFace).
    edgeOnly?: boolean;
    // the outline stick a frame placement of this item first mounts on: the head ("top"),
    // the sill ("bottom"), a jamb by side, or the hinge jamb of the active leaf ("hinge").
    // Without one it goes on the latch jamb.
    frameStick?: "top" | "bottom" | "left" | "right" | "hinge" | null;
    // where an unmodified frame placement of this item sits: the hub height from the top of
    // its stick. Without one it centres on the stick.
    frameStandardY?: ((frame: RuleFrame) => number) | null;
    // the prep a frame placement of this item cuts into the jamb, drawn on the Locations
    // view's jamb column (see JambView) centred on the placement's height and the door
    // thickness. Without one the placement shows as a tick.
    jambCutout?: {width: number; height: number} | null;
    // the mortise this item's door placement cuts into the door edge, drawn on the edge
    // views centred on the door thickness at the placement height.
    doorEdgeCutout?: {width: number; height: number} | null;
    // the prep this item's door placement starts linked to (see prepLinkFor): the 161 or
    // DB anchor (see MdHardware.prepAnchors) or the closer band. A linked placement
    // derives its position from the prep until the user unlinks it.
    linkTo?: PrepKind | null;
    // hub distance from the strike edge (the edge opposite the hinges).
    minFromStrikeEdge?: number | null;
    maxFromStrikeEdge?: number | null;
    // hub height above the finished floor (the leaf bottom plus the undercut).
    minAboveFloor?: number | null;
    maxAboveFloor?: number | null;
    // where Reset puts the hub, from the leaf it sits on. Without one, a primary handle /
    // lock goes back to its seeded lock height and backset, and anything else to the centre
    // of the leaf (see standardPosition).
    standardLocation?: ((leaf: RuleLeaf) => StandardLocation) | null;
};

// a standard hub location as the rules express it: from the strike edge and above the
// finished floor.
export type StandardLocation = {fromStrikeEdge: number; aboveFloor: number};

// the leaf a rule measures against: size, the undercut below the leaf bottom, and which
// edge the hinges are on.
export type RuleLeaf = {width: number; height: number; undercut: number; hingeSide: "left" | "right"};

// the frame a rule measures against: the jamb height and the hinges top-down, each by its
// centre from the top of the jamb and its height.
export type RuleFrame = {height: number; hinges: {center: number; height: number}[]};

// a closer body's standard centre: in from the hinge edge and down from the top of the leaf.
// An operator arm hangs the same distance in from the hinge edge.
export const CLOSER_FROM_HINGE_EDGE = 9 * inches;
const CLOSER_BELOW_TOP = 2 * inches;

// an operator forearm's standard centre line, down from the top of the leaf. The door
// placement's hub is the arm pivot on the leaf.
export const OPERATOR_ARM_BELOW_TOP = 2.5 * inches;

// an overhead stop's channel centre line, down from the top of the leaf.
const OH_STOP_BELOW_TOP = 0.5 * inches;

// the mortise an electrified power transfer cuts into the hinge jamb (and the door's hinge
// edge). Approximate: sized to a Von Duprin EPT-10 class unit.
const POWER_TRANSFER_CUTOUT = {width: 1.25 * inches, height: 7.75 * inches};
// the clearance between the power transfer's cutout and the hinge mortise below it.
const POWER_TRANSFER_ABOVE_HINGE = 0.5 * inches;

// the prep locations a door placement can link to: the 161 (cylindrical lock) hole, the
// DB (deadbolt) hole, or the closer reinforcing band across the top of the leaf. The
// drawing's prep layer draws each one in use; a linked placement sits on it.
export type PrepKind = "161" | "db" | "closer";
export const PREP_KINDS: PrepKind[] = ["161", "db", "closer"];
export function isPrepKind(v: unknown): v is PrepKind {
    return typeof v === "string" && (PREP_KINDS as string[]).includes(v);
}

// the preps that are a hole at a movable point.
export type AnchorKind = "161" | "db";

// an anchor as measurements on the leaf: the hole centre's height above the finished
// floor and its backset from the strike edge.
export type AnchorValues = {aboveFloor: number; fromStrike: number};

// the diameter of the 161 and DB holes on the prep layer.
export const PREP_HOLE_DIAMETER = (2 + 1 / 8) * inches;

// the closer band: the top of the leaf, this deep, across its full width.
export const CLOSER_PREP_DEPTH = 6 * inches;

// the standard location of a 161 / DB anchor: the standard backset from the strike edge at
// the centre-lock / deadbolt height above the leaf bottom.
export function standardAnchor(kind: AnchorKind, leaf: RuleLeaf): AnchorValues {
    return {
        aboveFloor: (kind === "161" ? CENTER_LOCK_ABOVE_BOTTOM : DEADBOLT_ABOVE_BOTTOM) + leaf.undercut,
        fromStrike: STANDARD_BACKSET,
    };
}

// the hub position (rect offsets) of an anchor on a leaf.
export function anchorPosition(leaf: RuleLeaf, anchor: AnchorValues): {x: number; y: number} {
    return {
        x: leaf.hingeSide === "right" ? anchor.fromStrike : leaf.width - anchor.fromStrike,
        y: leaf.height + leaf.undercut - anchor.aboveFloor,
    };
}

// the inverse of anchorPosition: the anchor measurements a hub position on a leaf means.
export function anchorFromPosition(leaf: RuleLeaf, pos: {x: number; y: number}): AnchorValues {
    return {
        fromStrike: leaf.hingeSide === "right" ? pos.x : leaf.width - pos.x,
        aboveFloor: leaf.height + leaf.undercut - pos.y,
    };
}

// the closer band on a leaf (rect offsets).
export function closerPrepRect(leaf: RuleLeaf): {x0: number; y0: number; x1: number; y1: number} {
    return {x0: 0, y0: 0, x1: leaf.width, y1: CLOSER_PREP_DEPTH};
}

// a door prep name that puts the item in the closer band: the "follow closer rule"
// option or the closer reinforcing preps it resolves to.
export function isCloserPrepName(name: string | undefined): boolean {
    const n = (name ?? "").toLowerCase();
    return n.includes("follow closer rule") || n.includes("closer reinf");
}

// the prep a door placement links to: the primary handle to the 161, the deadbolt to the
// DB, else what the item's category rule names, else the closer band for an item whose
// door prep follows the closer rule. Null for a frame placement or an item with no prep
// location.
export function prepLinkFor(item: HardwareItem | null, placement: Pick<MdHardwarePlacement, "target" | "kind">): PrepKind | null {
    if(placement.target !== "door") return null;
    if(placement.kind === "handle") return "161";
    if(placement.kind === "lock") return "db";
    if(!item) return null;

    const rule = ruleForItem(item)?.linkTo;
    if(rule) return rule;

    const doorPreps = [item.hwPrepSingleActiveDoor, item.hwPrepPairActiveDoor, item.hwPrepPairBothActiveDoor, item.hwPrepPairInactiveDoor];
    if(doorPreps.some(isCloserPrepName)) return "closer";
    return null;
}

// where a placement linked to `link` sits on its leaf: on the 161 / DB anchor, or in the
// closer band at the item's standard location (a closer's body spot) else the band's
// centre.
export function linkedPosition(leaf: RuleLeaf, link: PrepKind, item: HardwareItem | null, anchor: (kind: AnchorKind) => AnchorValues): {x: number; y: number} {
    if(link === "161" || link === "db") return anchorPosition(leaf, anchor(link));

    const loc = item ? ruleForItem(item)?.standardLocation : null;
    if(loc) {
        const l = loc(leaf);
        return {
            x: leaf.hingeSide === "right" ? l.fromStrikeEdge : leaf.width - l.fromStrikeEdge,
            y: leaf.height + leaf.undercut - l.aboveFloor,
        };
    }
    const band = closerPrepRect(leaf);
    return {x: (band.x0 + band.x1) / 2, y: (band.y0 + band.y1) / 2};
}

// a protection plate's standard centre: across the middle of the leaf, sitting on its
// bottom edge.
function bottomPlate(height: number): (leaf: RuleLeaf) => StandardLocation {
    return leaf => ({fromStrikeEdge: leaf.width / 2, aboveFloor: leaf.undercut + height / 2});
}

export const HARDWARE_PLACEMENT_RULES: HardwarePlacementRule[] = [
    {
        category: "Exit Devices",
        face: "push",
        maxFromStrikeEdge: 5 * inches,
        minAboveFloor: 36 * inches,
        linkTo: "161",
    },
    {
        category: "Exit Trims",
        face: "pull",
        linkTo: "161",
    },
    {
        category: "Door Pull",
        face: "pull",
        linkTo: "161",
    },
    {
        category: "Pull Plate",
        face: "pull",
    },
    {
        category: "Push Bar",
        face: "push",
    },
    {
        category: "Mop Plate",
        face: "pull",
        standardLocation: bottomPlate(MOP_PLATE_HEIGHT),
    },
    {
        category: "Armour Plate",
        face: "push",
        standardLocation: bottomPlate(ARMOUR_PLATE_HEIGHT),
    },
    {
        // parallel-arm mount on the push side: the body centre just under the top of the
        // leaf, in from the hinge edge.
        category: "Closers",
        face: "push",
        linkTo: "closer",
        standardLocation: leaf => ({
            fromStrikeEdge: leaf.width - CLOSER_FROM_HINGE_EDGE,
            aboveFloor: leaf.height + leaf.undercut - CLOSER_BELOW_TOP,
        }),
    },
    {
        // the track in the head, the channel in the top of the leaf: centred across the
        // leaf, just under its top.
        category: "Overhead Stops",
        frameStick: "top",
        standardLocation: leaf => ({
            fromStrikeEdge: leaf.width / 2,
            aboveFloor: leaf.height + leaf.undercut - OH_STOP_BELOW_TOP,
        }),
    },
    {
        // the header mounts on the face of the head; the arm pivot on the push side of
        // the leaf, in from the hinge edge like a closer, just under the top.
        category: "Surface Automatic Operator",
        face: "push",
        frameStick: "top",
        linkTo: "closer",
        standardLocation: leaf => ({
            fromStrikeEdge: leaf.width - CLOSER_FROM_HINGE_EDGE,
            aboveFloor: leaf.height + leaf.undercut - OPERATOR_ARM_BELOW_TOP,
        }),
    },
    {
        // mortised into the door's hinge edge (nothing shows on a face) and into the hinge
        // jamb, just above the second hinge from the floor.
        category: "Power Transfer",
        edgeOnly: true,
        doorEdgeCutout: POWER_TRANSFER_CUTOUT,
        frameStick: "hinge",
        jambCutout: POWER_TRANSFER_CUTOUT,
        frameStandardY: frame => {
            const hinge = frame.hinges[frame.hinges.length - 2];
            if(!hinge) return frame.height / 2;
            return hinge.center - hinge.height / 2 - POWER_TRANSFER_ABOVE_HINGE - POWER_TRANSFER_CUTOUT.height / 2;
        },
    },
];

// the standard hub height of an unmodified frame placement of this item from the top of
// its stick, or null when its rule has none (it centres on the stick).
export function standardFrameY(app: ElevationApp, item: HardwareItem): number | null {
    return standardFrameYOn(item, app.frameSpec.nominalHeight.get(), app.hardware.hinges);
}

// standardFrameY from the model parts, for a caller without the app (MdHardware).
export function standardFrameYOn(item: HardwareItem, frameHeight: number, hinges: MdHinges): number | null {
    const rule = ruleForItem(item)?.frameStandardY;
    if(!rule) return null;
    return rule({
        height: frameHeight,
        hinges: hinges.list.map((h, i) => ({center: h.offsetTop.get(), height: hinges.hingeHeightAt(i)})),
    });
}

// the jamb a frame placement of this item belongs to on the Locations view: the hinge
// jamb when its rule mounts it there, else the strike jamb. Null for an item ruled onto
// the head or sill.
export function frameJambOf(item: Pick<HardwareItem, "category" | "category2" | "category3">): "hinge" | "strike" | null {
    const stick = ruleForItem(item)?.frameStick;
    if(stick === "top" || stick === "bottom") return null;
    return stick === "hinge" ? "hinge" : "strike";
}

// the face an item's hardware mounts on, or null when it shows on both.
export function itemFace(item: Pick<HardwareItem, "category" | "category2" | "category3">): DoorFace | null {
    return ruleForItem(item)?.face ?? null;
}

// whether a view showing `face` of a leaf draws the item: it mounts on that face, or on
// both, or the leaf has no single swing (null face) so every item shows.
export function mountsOnFace(item: Pick<HardwareItem, "category" | "category2" | "category3">, face: DoorFace | null): boolean {
    const mount = itemFace(item);
    return mount === null || face === null || mount === face;
}

// the door edge an edge-only item lives in: the hinge edge when its rule mounts its frame
// half on the hinge jamb (a power transfer), else the latch edge. Null for an item that
// shows on a face.
export function doorEdgeOf(item: Pick<HardwareItem, "category" | "category2" | "category3">): "hinge" | "latch" | null {
    const rule = ruleForItem(item);
    if(!rule?.edgeOnly) return null;
    return rule.frameStick === "hinge" ? "hinge" : "latch";
}

// whether a leaf face showing `face` draws the item's door placement: an edge-only item
// (see HardwarePlacementRule.edgeOnly) never does; otherwise as mountsOnFace.
export function drawnOnDoorFace(item: Pick<HardwareItem, "category" | "category2" | "category3">, face: DoorFace | null): boolean {
    if(ruleForItem(item)?.edgeOnly) return false;
    return mountsOnFace(item, face);
}

export type HardwarePlacementViolation = {
    // the category whose rule failed; the item's own for a lite overlap.
    category: string;
    message: string;
};

// the merged rule for an item: its category path root-first (category3 > category2 >
// category), each level's rule laid over the one above it.
export function ruleForItem(item: Pick<HardwareItem, "category" | "category2" | "category3">, rules: HardwarePlacementRule[] = HARDWARE_PLACEMENT_RULES): HardwarePlacementRule | null {
    const path = [item.category3, item.category2, item.category].filter((c): c is string => !!c);

    let merged: HardwarePlacementRule | null = null;
    for(const level of path) {
        const rule = rules.find(r => r.category.toLowerCase() === level.toLowerCase());
        if(!rule) continue;
        merged = {...(merged ?? {}), ...rule};
    }

    return merged;
}

// the leaf a door placement sits on, from the specs that describe it. Falls back to the
// active-leaf hinge convention (active hinges left, inactive right) for handings with no
// swing.
export function leafGeometry(frameSpec: MdFrameSpec, doorSpec: MdDoorSpec, side: "active" | "inactive"): RuleLeaf {
    const active = side === "active";
    let hingeSide: "left" | "right";
    try {
        hingeSide = swingSideFor(frameSpec, active).side;
    } catch {
        hingeSide = active ? "left" : "right";
    }
    return {
        width: active ? doorSpec.activeDoorWidth.get() : doorSpec.inActiveDoorWidth.get(),
        height: doorSpec.actualHeight.get(),
        undercut: doorSpec.undercut.get(),
        hingeSide,
    };
}

function leafFor(app: ElevationApp, placement: MdHardwarePlacement): RuleLeaf {
    return leafGeometry(app.frameSpec, app.doorSpec, placement.side);
}

// whether a door placement's rule gives it a standard position of its own (a standard
// location, or a prep to link to), rather than the leaf centre.
export function hasStandardPosition(item: HardwareItem): boolean {
    const rule = ruleForItem(item);
    return !!rule && (!!rule.standardLocation || !!rule.linkTo);
}

// the standard hub position (rect offsets) for a door placement: on the prep it links to
// (see linkedPosition), else the category rule's standard location, else the leaf centre.
export function standardPosition(app: ElevationApp, placement: MdHardwarePlacement, item: HardwareItem): {x: number; y: number} {
    return standardPositionOn(leafFor(app, placement), placement, item, kind => app.hardware.anchor(placement.side, kind));
}

// standardPosition on a given leaf, with the leaf's anchors supplied.
export function standardPositionOn(leaf: RuleLeaf, placement: Pick<MdHardwarePlacement, "target" | "kind">, item: HardwareItem | null, anchor: (kind: AnchorKind) => AnchorValues): {x: number; y: number} {
    const link = prepLinkFor(item, placement);
    if(link) return linkedPosition(leaf, link, item, anchor);

    const rule = item ? ruleForItem(item) : null;
    if(rule?.standardLocation) {
        const loc = rule.standardLocation(leaf);
        return {
            x: leaf.hingeSide === "right" ? loc.fromStrikeEdge : leaf.width - loc.fromStrikeEdge,
            y: leaf.height + leaf.undercut - loc.aboveFloor,
        };
    }

    return {x: leaf.width / 2, y: leaf.height / 2};
}

// keep an intended door placement position inside its item's rule limits. Returns the
// position unchanged when no rule applies, the rule only flags, or it's a frame placement.
// Applied by HardwareCrosshairThing to drags and dimension edits before they're stored.
export function clampToRule(app: ElevationApp, placement: MdHardwarePlacement, item: HardwareItem, pos: {x: number; y: number}): {x: number; y: number} {
    if(placement.target !== "door") return pos;

    const rule = ruleForItem(item);
    if(!rule || (rule.enforce ?? "clamp") !== "clamp") return pos;

    const leaf = leafFor(app, placement);
    let {x, y} = pos;

    // strike-edge distance -> x, per which edge the strike is on.
    const toX = (fromStrike: number) => leaf.hingeSide === "right" ? fromStrike : leaf.width - fromStrike;
    const fromStrike = leaf.hingeSide === "right" ? x : leaf.width - x;
    if(rule.maxFromStrikeEdge != null && fromStrike > rule.maxFromStrikeEdge) x = toX(rule.maxFromStrikeEdge);
    if(rule.minFromStrikeEdge != null && fromStrike < rule.minFromStrikeEdge) x = toX(rule.minFromStrikeEdge);

    // floor height -> y, measured down from the leaf top.
    const toY = (aboveFloor: number) => leaf.height + leaf.undercut - aboveFloor;
    const aboveFloor = toY(y);
    if(rule.minAboveFloor != null && aboveFloor < rule.minAboveFloor) y = toY(rule.minAboveFloor);
    if(rule.maxAboveFloor != null && aboveFloor > rule.maxAboveFloor) y = toY(rule.maxAboveFloor);

    return {x, y};
}

// check a door placement against its item's merged rule, and against the leaf's lites.
// Empty when it passes. `fmt` renders a distance for the messages.
export function doorPlacementViolations(app: ElevationApp, placement: MdHardwarePlacement, item: HardwareItem, fmt: (mm: number) => string): HardwarePlacementViolation[] {
    if(placement.target !== "door") return [];

    const out: HardwarePlacementViolation[] = [];

    const rule = ruleForItem(item);
    if(rule) {
        const leaf = leafFor(app, placement);
        const x = placement.x.get();
        const y = placement.y.get();
        const fromStrike = leaf.hingeSide === "right" ? x : leaf.width - x;
        const aboveFloor = leaf.height - y + leaf.undercut;

        const fail = (message: string) => out.push({category: rule.category, message});

        if(rule.maxFromStrikeEdge != null && fromStrike > rule.maxFromStrikeEdge) {
            fail(`must be within ${fmt(rule.maxFromStrikeEdge)} of the strike edge (now ${fmt(fromStrike)})`);
        }
        if(rule.minFromStrikeEdge != null && fromStrike < rule.minFromStrikeEdge) {
            fail(`must be at least ${fmt(rule.minFromStrikeEdge)} from the strike edge (now ${fmt(fromStrike)})`);
        }
        if(rule.minAboveFloor != null && aboveFloor < rule.minAboveFloor) {
            fail(`must be at least ${fmt(rule.minAboveFloor)} above the floor (now ${fmt(aboveFloor)})`);
        }
        if(rule.maxAboveFloor != null && aboveFloor > rule.maxAboveFloor) {
            fail(`must be within ${fmt(rule.maxAboveFloor)} of the floor (now ${fmt(aboveFloor)})`);
        }
    }

    for(const o of liteOverlapsOf(app, placement, item)) {
        out.push({category: item.category, message: `overlaps lite ${o.lite.mark} by ${fmt(o.overlap.x1 - o.overlap.x0)} × ${fmt(o.overlap.y1 - o.overlap.y0)}`});
    }

    return out;
}
